import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Edit2, Trash2, X, User, DollarSign, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface Employee {
  id: string;
  full_name: string;
  role: string;
}

interface Bonus {
  id: string;
  profile_id: string;
  amount: number;
  description: string | null;
  bonus_date: string;
  notes: string | null;
  created_at: string;
}

const EMPLOYEE_ROLES = [
  'Superadmin', 'Admin',
  'Dispatch', 'Dispatch Manager',
  'Safety', 'Safety Manager',
  'Accounting', 'Accounting Manager',
  'Recruiting', 'Afterhours', 'ELD',
];

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function EmployeeBonusesView() {
  const toast = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState<string>('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Bonus | null>(null);
  const [form, setForm] = useState({
    profile_id: '',
    amount: '',
    description: '',
    bonus_date: todayISO(),
    notes: '',
  });

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [empRes, bonRes] = await Promise.all([
        supabase.from('profiles').select('id, full_name, role').in('role', EMPLOYEE_ROLES).order('full_name'),
        supabase.from('employee_bonuses').select('*').order('bonus_date', { ascending: false }),
      ]);
      if (empRes.error) throw empRes.error;
      if (bonRes.error) throw bonRes.error;
      setEmployees((empRes.data || []) as Employee[]);
      setBonuses((bonRes.data || []) as Bonus[]);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to load bonuses');
    } finally {
      setLoading(false);
    }
  }

  const employeeById = useMemo(() => {
    const m = new Map<string, Employee>();
    employees.forEach(e => m.set(e.id, e));
    return m;
  }, [employees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bonuses.filter(b => {
      if (employeeFilter && b.profile_id !== employeeFilter) return false;
      if (!q) return true;
      const emp = employeeById.get(b.profile_id);
      const name = emp?.full_name?.toLowerCase() || '';
      const desc = (b.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [bonuses, employeeFilter, employeeById, search]);

  const total = useMemo(() => filtered.reduce((s, b) => s + Number(b.amount || 0), 0), [filtered]);

  function openAdd() {
    setEditing(null);
    setForm({
      profile_id: employeeFilter || '',
      amount: '',
      description: '',
      bonus_date: todayISO(),
      notes: '',
    });
    setShowModal(true);
  }

  function openEdit(b: Bonus) {
    setEditing(b);
    setForm({
      profile_id: b.profile_id,
      amount: String(b.amount),
      description: b.description || '',
      bonus_date: b.bonus_date,
      notes: b.notes || '',
    });
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.profile_id) {
      toast.error('Select an employee');
      return;
    }
    const amountNum = parseFloat(form.amount);
    if (!Number.isFinite(amountNum) || amountNum === 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const payload = {
      profile_id: form.profile_id,
      amount: amountNum,
      description: form.description.trim() || null,
      bonus_date: form.bonus_date,
      notes: form.notes.trim() || null,
      updated_at: new Date().toISOString(),
    };
    try {
      if (editing) {
        const { error } = await supabase.from('employee_bonuses').update(payload).eq('id', editing.id);
        if (error) throw error;
        toast.success('Bonus updated');
      } else {
        const { error } = await supabase.from('employee_bonuses').insert(payload);
        if (error) throw error;
        toast.success('Bonus added');
      }
      setShowModal(false);
      load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
    }
  }

  async function handleDelete(b: Bonus) {
    if (!confirm('Delete this bonus?')) return;
    try {
      const { error } = await supabase.from('employee_bonuses').delete().eq('id', b.id);
      if (error) throw error;
      toast.success('Bonus deleted');
      load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Plus className="h-6 w-6 text-green-600" />
            Employee Bonuses
          </h1>
          <p className="text-sm text-slate-500 mt-1">Add one-off bonuses that appear on an employee's statement.</p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" /> Add Bonus
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by employee name or description..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={employeeFilter}
          onChange={e => setEmployeeFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All employees</option>
          {employees.map(e => (
            <option key={e.id} value={e.id}>{e.full_name} — {e.role}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200 text-sm text-slate-600">
          <span>{filtered.length} bonus{filtered.length === 1 ? '' : 'es'}</span>
          <span className="font-medium text-slate-800">Total: ${total.toFixed(2)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Employee</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Role</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Amount</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No bonuses found.</td></tr>
              )}
              {!loading && filtered.map(b => {
                const emp = employeeById.get(b.profile_id);
                return (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-sm text-slate-800 flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      {emp?.full_name || '—'}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-600">{emp?.role || '—'}</td>
                    <td className="px-4 py-2 text-sm text-slate-600">{b.bonus_date}</td>
                    <td className="px-4 py-2 text-sm text-right font-medium text-green-700">${Number(b.amount).toFixed(2)}</td>
                    <td className="px-4 py-2 text-sm text-slate-600">{b.description || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => openEdit(b)}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(b)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">
                {editing ? 'Edit Bonus' : 'Add Bonus'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Employee *</label>
                <select
                  value={form.profile_id}
                  onChange={e => setForm(f => ({ ...f, profile_id: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2"
                >
                  <option value="">Select employee…</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.full_name} — {emp.role}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Amount *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Date *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="date"
                    value={form.bonus_date}
                    onChange={e => setForm(f => ({ ...f, bonus_date: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2"
                  placeholder="e.g. Quarterly performance bonus"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 h-20 resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={handleSave} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                {editing ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
